import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReadinessLevel } from "../types";

type ReadinessDialSize = "sm" | "md" | "lg" | "xl";

const levelTone: Record<ReadinessLevel, string> = {
  RED: "var(--color-danger)",
  AMBER: "var(--color-warning)",
  GREEN: "var(--color-success)",
};

const gradientStops: Record<ReadinessLevel, { start: string; end: string }> = {
  RED: { start: "var(--color-danger-border)", end: "var(--color-danger)" },
  AMBER: { start: "var(--color-warning-border)", end: "var(--color-warning)" },
  GREEN: { start: "var(--color-success-border)", end: "var(--color-success)" },
};

const sizeConfig: Record<
  ReadinessDialSize,
  {
    className: string;
    svg: number;
    radius: number;
    stroke: number;
    text: string;
    label: string;
    bar: string;
  }
> = {
  sm: {
    className: "h-10 w-10",
    svg: 40,
    radius: 16,
    stroke: 3.5,
    text: "text-[0.8rem]",
    label: "sr-only",
    bar: "h-0.5 w-5",
  },
  md: {
    className: "h-16 w-16",
    svg: 64,
    radius: 26,
    stroke: 4,
    text: "text-lg",
    label: "sr-only",
    bar: "h-0.5 w-7",
  },
  lg: {
    className: "h-24 w-24",
    svg: 96,
    radius: 40,
    stroke: 5,
    text: "text-2xl",
    label: "sr-only",
    bar: "h-1 w-10",
  },
  xl: {
    className: "h-32 w-32",
    svg: 128,
    radius: 54,
    stroke: 6,
    text: "text-4xl",
    label: "mt-1 text-[0.6875rem]",
    bar: "h-1 w-14",
  },
};

function clampScore(score: number) {
  return Math.min(100, Math.max(0, Math.round(score)));
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return true;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ReadinessDial({
  score,
  level,
  size = "md",
  label = "Detail",
  caption,
  showZoneBar = true,
  intent = "score",
  delta,
  secondaryRing = false,
  glow = false,
}: {
  score: number;
  level: ReadinessLevel;
  size?: ReadinessDialSize;
  label?: string;
  caption?: string;
  showZoneBar?: boolean;
  intent?: "score" | "delta";
  delta?: number;
  secondaryRing?: boolean;
  glow?: boolean;
}) {
  const safeScore = clampScore(score);
  const config = sizeConfig[size];
  const dialId = useId().replace(/:/g, "");
  const gradientId = `dial-${dialId}-${level.toLowerCase()}`;
  const wellId = `dial-well-${dialId}`;
  const circumference = useMemo(() => 2 * Math.PI * config.radius, [config.radius]);
  const [displayScore, setDisplayScore] = useState(safeScore);
  const [showHalo, setShowHalo] = useState(false);
  const previousScore = useRef(displayScore);
  const previousLevel = useRef(level);
  const dashOffset = circumference * (1 - safeScore / 100);

  useEffect(() => {
    if (prefersReducedMotion()) {
      const timeout = window.setTimeout(() => {
        previousScore.current = safeScore;
        setDisplayScore(safeScore);
      }, 0);
      return () => window.clearTimeout(timeout);
    }

    let frame = 0;
    const start = previousScore.current;
    const delta = safeScore - start;
    const totalFrames = 36;
    let animation = 0;

    const tick = () => {
      frame += 1;
      const progress = Math.min(1, frame / totalFrames);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextScore = clampScore(start + delta * eased);
      previousScore.current = nextScore;
      setDisplayScore(nextScore);
      if (progress < 1) {
        animation = window.requestAnimationFrame(tick);
      }
    };

    animation = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(animation);
  }, [safeScore]);

  useEffect(() => {
    if (previousLevel.current === level || prefersReducedMotion()) {
      previousLevel.current = level;
      return;
    }
    setShowHalo(true);
    previousLevel.current = level;
    const timeout = window.setTimeout(() => setShowHalo(false), 700);
    return () => window.clearTimeout(timeout);
  }, [level]);

  return (
    <span className="inline-grid justify-items-center gap-2">
      <span
        aria-label={`${label} ${safeScore} of 100, ${level.toLowerCase()}`}
        className={`relative inline-flex ${config.className} shrink-0 items-center justify-center ${glow ? "dial-glow" : ""}`}
        role="img"
      >
        <svg
          aria-hidden="true"
          className="absolute inset-0"
          height={config.svg}
          viewBox={`0 0 ${config.svg} ${config.svg}`}
          width={config.svg}
        >
          <defs>
            <radialGradient id={gradientId} cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor={gradientStops[level].start} />
              <stop offset="100%" stopColor={gradientStops[level].end} />
            </radialGradient>
            <radialGradient id={wellId} cx="50%" cy="50%" r="58%">
              <stop offset="0%" stopColor="var(--color-paper)" stopOpacity="1" />
              <stop offset="100%" stopColor="var(--color-surface-sunken)" stopOpacity="0.62" />
            </radialGradient>
          </defs>
          <circle
            cx={config.svg / 2}
            cy={config.svg / 2}
            fill={`url(#${wellId})`}
            r={Math.max(1, config.radius - config.stroke - 2)}
          />
          <circle
            cx={config.svg / 2}
            cy={config.svg / 2}
            fill="none"
            r={Math.max(1, config.radius - config.stroke - 1)}
            stroke="rgba(0,0,0,0.04)"
            strokeWidth="1"
          />
          <circle
            className="dial-track"
            cx={config.svg / 2}
            cy={config.svg / 2}
            fill="none"
            r={config.radius}
            strokeDasharray="3 7"
            strokeLinecap="round"
            strokeWidth={config.stroke}
          />
          {showHalo ? (
            <circle
              className="dial-halo"
              cx={config.svg / 2}
              cy={config.svg / 2}
              fill="none"
              r={Math.min(config.svg / 2 - 2, config.radius + config.stroke)}
              stroke={levelTone[level]}
              strokeWidth="1"
            />
          ) : null}
          {secondaryRing ? (
            <circle
              aria-hidden="true"
              className="dial"
              cx={config.svg / 2}
              cy={config.svg / 2}
              fill="none"
              r={Math.min(config.svg / 2 - 3, config.radius * 1.15)}
              stroke="var(--color-border-strong)"
              strokeWidth="1"
            />
          ) : null}
          <circle
            className="dial dial-fill"
            cx={config.svg / 2}
            cy={config.svg / 2}
            fill="none"
            r={config.radius}
            stroke={`url(#${gradientId})`}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            strokeWidth={config.stroke}
            style={{ color: levelTone[level] }}
          />
        </svg>
        <span className="relative grid place-items-center text-center">
          <span
            className={`font-display num font-semibold text-text ${size === "xl" ? "readiness-digit-xl" : ""} ${config.text}`}
          >
            {displayScore}
          </span>
          {showZoneBar ? (
            <span
              aria-hidden="true"
              className={`relative mt-1 block overflow-hidden rounded-pill ${config.bar}`}
              style={{ backgroundColor: levelTone[level] }}
            >
              {[0, 1].map((index) => (
                <span
                  key={index}
                  className="absolute top-1/2 h-1 w-px -translate-y-1/2 rounded-pill bg-paper/80"
                  style={{ left: `${(index + 1) * 33.333}%` }}
                />
              ))}
            </span>
          ) : null}
          <span
            className={`font-bold uppercase leading-none tracking-[var(--tracking-eyebrow)] text-accent-700 ${config.label}`}
          >
            {label}
          </span>
          {intent === "delta" && typeof delta === "number" && delta !== 0 ? (
            <span className="mt-1 text-xs font-semibold text-success tabular">
              {delta > 0 ? "+" : ""}
              {delta}
            </span>
          ) : null}
        </span>
        <span className="sr-only">
          {level} {safeScore}/100
        </span>
      </span>
      {caption ? (
        <span className="text-balance text-center text-xs font-semibold tracking-[0.04em] text-text-muted">
          {caption}
        </span>
      ) : null}
    </span>
  );
}
